import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query'
import { useRef } from 'react'

export class MutationGatedError extends Error {
  constructor() {
    super('Mutation is already in progress')
    this.name = 'MutationGatedError'
  }
}

/**
 * Wraps a mutateAsync so only one call runs at a time: a call while one is
 * in flight rejects with MutationGatedError before it reaches TanStack, so
 * no mutation is built and the observer keeps following the live call.
 * Only the call that took the gate releases it, once fully settled.
 */
export function gateMutateAsync<TArgs extends unknown[], TData>(
  mutateAsync: (...args: TArgs) => Promise<TData>,
  gate: { current: boolean },
): (...args: TArgs) => Promise<TData> {
  return async (...args) => {
    if (gate.current) {
      throw new MutationGatedError()
    }
    gate.current = true
    try {
      return await mutateAsync(...args)
    } finally {
      gate.current = false
    }
  }
}

/**
 * useMutation whose mutate/mutateAsync go through gateMutateAsync. A gated
 * call never runs the options' callbacks; mutateAsync rejects with
 * MutationGatedError and mutate drops it silently.
 */
export function useGatedMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TOnMutateResult = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
): UseMutationResult<TData, TError, TVariables, TOnMutateResult> {
  const gateRef = useRef(false)
  const mutation = useMutation(options)
  const mutateAsync = gateMutateAsync(mutation.mutateAsync, gateRef)

  return {
    ...mutation,
    mutate: (variables, mutateOptions) => {
      mutateAsync(variables, mutateOptions).catch(() => {})
    },
    mutateAsync,
  }
}
